import { Button, FormGroup, Input, Label, Select } from '@/components'
import { FlexWrapper } from '@/components/layout/flex/flexWrapper'
import { useAxios } from '@/context/axiosContext'
import { useEffect, useRef, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useLocation, useNavigate } from 'react-router-dom'
import commonUtils from '@/utils/commonUtils'
import { useRedis } from '@/hooks/useRedis'
import { useLog } from '@/hooks/useLog'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

interface DataRow {
  cmpnyCd: string
  logId: string
  regId: string
  regDate: string
  updId: string
  updDate: string
  siteType: string
  siteTypeNm: string
  logLayer: string
  logLayerNm: string
  logDate: string
  logTime: string
  logType: string
  logTypeNm: string
  clientIp: string
  logMsg: string
  remark: string
  stackTrace: string
  inqyStartDate: string
  inqyEndDate: string
}

const ComLog: React.FC = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<DataRow[]>([]);
  const [totalCnt, setTotalCnt] = useState(0);

  const { getComCodes, getComCodeValue } = useRedis();

  const {insertLog} = useLog();

  // const [currentPage, setCurrentPage] = useState(location.state?.page || 1);
  const currentPage = useRef(location.state?.page || 1);
  const perPage = useRef(location.state?.perPage || 10);
  const today = new Date();
  const [searchData, setSearchData] = useState<DataRow>(
    location.state?.searchData || {
      cmpnyCd: '',
      logId: '',
      regId: '',
      regDate: '',
      updId: '',
      updDate: '',
      siteType: '',
      siteTypeNm: '',
      logLayer: '',
      logDate: '',
      logTime: '',
      logType: '',
      clientIp: '',
      logMsg: '',
      remark: '',
      stackTrace: '',
      inqyStartDate: today.toISOString().split('T')[0],
      inqyEndDate: today.toISOString().split('T')[0],
    },
  )

  const [inqyData, setInqyData] = useState<DataRow>(searchData);

  const columns: TableColumn<DataRow>[] = [
    // {
    //   name: 'No.',
    //   selector: (row) => row.rnum,
    //   sortable: true,
    //   center: true,
    //   wrap: true,
    // },
    {
      name: '로그ID',
      selector: (row) => row.logId,
      sortable: true,
      wrap: true,
      width: '12%',
      cell: (row) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.logId}</div>
      ),
    },
    {
      name: '로그발생일시',
      selector: (row) =>
        commonUtils.formatDate(row.logDate + row.logTime, true),
      sortable: true,
      center: true,
      wrap: true,
      width: '12%',
    },
    {
      name: '사이트유형',
      selector: (row) => row.siteTypeNm || row.siteType,
      sortable: true,
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '레이어',
      selector: (row) => row.logLayerNm || row.logLayer,
      sortable: true,
      center: true,
      wrap: true,
      width: '8%',
    },
    {
      name: '로그유형',
      selector: (row) => row.logTypeNm || row.logType,
      sortable: true,
      center: true,
      wrap: true,
      conditionalCellStyles: [
        {
          when: (row: DataRow) => row.logType === 'ERROR',
          style: {
            color: 'red',
          },
        },
      ],
      width: '10%',
    },
    {
      name: '메세지',
      selector: (row) => row.logMsg,
      sortable: true,
      center: true,
      wrap: true,
      width: '40%',
      // style: {
      //     overflow: 'hidden', // 넘치는 텍스트 숨기기
      //     whiteSpace: 'nowrap', // 텍스트를 한 줄로 유지
      //     textOverflow: 'ellipsis', // 말줄임표로 표시
      // },
      cell: (row: DataRow) => (
        <span
          style={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            // whiteSpace: 'nowrap', // 텍스트를 한 줄로 유지
            // textOverflow: 'ellipsis', // 말줄임표로 표시
            textAlign: 'left',
            width: '100%',
          }}
        >
          {row.logMsg}
        </span>
      ),
    },
    {
      name: '',
      cell: (row) => (
        <>
          <Button
            variant="outlined"
            color="neverbe"
            size="xs"
            onClick={() => handleButtonUpdateClick(row.logId)}
          >
            상세
          </Button>
        </>
      ),
      sortable: false,
      width: '10%',
    },
  ]

  const handleButtonUpdateClick = async (logId: string) => {
    navigate(`/comLog/detail`, {
      state: {
        logId: logId,
        searchData: inqyData,
        page: currentPage.current, 
        perPage: perPage.current
      },
    })
  }

  const getComLogList = async () => {
    try {
      const response = await axios.post('/log/list', {...inqyData, inqyStartDate: searchData.inqyStartDate.replaceAll('-', ''), inqyEndDate: searchData.inqyEndDate.replaceAll('-', ''), page: currentPage.current, perPage: perPage.current});

      const listData:DataRow[] = response.data.data.list;
      if (Array.isArray(listData)) {
        for(const logData of listData){
          logData.siteTypeNm = getComCodeValue('SITE_TYPE', logData.siteType)?.codeNm || '';
          logData.logTypeNm = getComCodeValue('LOG_TYPE', logData.logType)?.codeNm || '';
          logData.logLayerNm = getComCodeValue('LOG_LAYER', logData.logLayer)?.codeNm || '';
        }
        setData(listData);
      }
      setTotalCnt(response.data.data.totalCnt);
    } catch (error:any) {
      insertLog({
        logMsg: error.message,
        stackTrace: error.stack,
        remarks : 'call api /log/list',
      });
    }
  }

  const handleInputChange = (e: React.ChangeEvent<TInput>) => {
    const { name, value } = e.target
    setSearchData((prevState) => {
      return {
        ...prevState,
        [name]: value,
      } as DataRow // 모든 필드를 포함하는 상태를 유지
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }

  const handleSearch = () => {
    changePage(1);
  };

  const changePage = (page:number) => {
    currentPage.current = page;
    setInqyData({
      ...searchData,
    });
  }
  const changePerPage = (page:number) => {
    perPage.current = page;
    getComLogList();
  }

  useEffect(() => {
    getComLogList();
  }, [inqyData])

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: '10px',
          alignItems: 'center',
          marginTop: '50px',
        }}
      >
        <p style={{ marginLeft: '20px', fontSize: '20px' }}>로그관리</p>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <FormGroup>
              <Label htmlFor={'logMsg'}>조회시작일</Label>
              <FlexWrapper>
                <Input
                  placeholder="YYYYMMDD"
                  id="inqyStartDate"
                  name="inqyStartDate"
                  type='date'
                  value={searchData.inqyStartDate}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={'logMsg'}>조회종료일</Label>
              <FlexWrapper>
                <Input
                  placeholder="YYYYMMDD"
                  id="inqyEndDate"
                  name="inqyEndDate"
                  type='date'
                  value={searchData.inqyEndDate}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'siteType'}>사이트유형</Label>
              <Select
                id="siteType"
                name="siteType"
                width="100px"
                onChange={handleInputChange}
                value={searchData.siteType}
              >
                <option value="">전체</option>
                {getComCodes('SITE_TYPE').map((item) => (
                  <option key={'siteType'+item.code} value={item.code}>
                      {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'logLayer'}>레이어</Label>
              <Select
                id="logLayer"
                name="logLayer"
                width="120px"
                onChange={handleInputChange}
                value={searchData.logLayer}
              >
                <option value="">전체</option>
                {getComCodes('LOG_LAYER').map((item) => (
                  <option key={'logLayer'+item.code} value={item.code}>
                      {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'logType'}>로그유형</Label>
              <Select
                id="logType"
                name="logType"
                width="100px"
                onChange={handleInputChange}
                value={searchData.logType}
              >
                <option value="">전체</option>
                {getComCodes('LOG_TYPE').map((item) => (
                  <option key={'logType'+item.code} value={item.code}>
                      {item.codeNm}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor={'logMsg'}>
                <></>
              </Label>
              <FlexWrapper>
                <Input
                  placeholder="메세지"
                  width="300px"
                  id="logMsg"
                  name="logMsg"
                  maxLength={100}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
              </FlexWrapper>
            </FormGroup>
            <FormGroup>
              <Label htmlFor={''}>
                <></>
              </Label>
              <FlexWrapper gap="10px">
                <Button
                  onClick={handleSearch}
                  color="neverbe"
                  variant="outlined"
                >
                  검색
                </Button>
              </FlexWrapper>
            </FormGroup>
          </div>
        </div>
      </div>
      <DataTable
        columns={columns}                                     // 컬럼정보(header)
        data={data}                                           // 화면에 표시될 데이터
        pagination={true}                                     // 페이징 여부
        paginationServer={true}                               // 페이징 서버에서 처리(서버페이징)
        paginationDefaultPage={currentPage.current}           // 첫페이지 설정
        onChangePage={(page) =>                               // 페이지 변경 이벤트
          page != currentPage.current && changePage(page)}    // > currentPage.current 직접변경시 중복호출 방지 
        paginationPerPage={perPage.current}                   // 한화면에 보여줄 데이터 수
        onChangeRowsPerPage={(page) => changePerPage(page)}   // 한화면에 보여줄 데이터 변경 이벤트
        paginationTotalRows={totalCnt}                        // 전체 데이터 수(서버페이징)
        paginationRowsPerPageOptions={[10, 20, 30, 50]}       // 한화면에 보여줄 데이터 셀렉트박스
        sortServer={true}                                     // 서버에서 정렬함(비활성화)
        sortIcon={<></>}                                      // 정렬아이콘 공백처리
        noDataComponent={<>조회결과가 없습니다.</>}
      />
    </>
  )
}
export default ComLog
